import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import roomManageService from '../services/roomManageService';
import styles from '../assets/roomManage.module.css';

const RoomManage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [room, setRoom] = useState(null);
  const [property, setProperty] = useState(null);
  const [selectedGuardians, setSelectedGuardians] = useState([]);
  const [guardianOfficerId, setGuardianOfficerId] = useState('');
  const [officers, setOfficers] = useState([]);
  const [propertySearch, setPropertySearch] = useState('');
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [payments, setPayments] = useState({});
  const [utilities, setUtilities] = useState({});
  const [errors, setErrors] = useState({});
  const [moveGuardian, setMoveGuardian] = useState(false);
  const [disabledRents, setDisabledRents] = useState([]);

  const loadRoomData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Loading room data for roomId:', roomId);
      const response = await roomManageService.getRoomManageData(roomId);
      console.log('Room data response:', response);
      
      if (response.success) {
        setRoom(response.data.room);
        setProperty(response.data.property);
        setOfficers(response.data.officers);
        setSelectedGuardians(response.data.guardians || []);
        setMoveGuardian(response.data.moveGuardian || false);
        setDisabledRents(response.data.disabledRents || []);
        
        // Initialize payments and utilities
        const initialPayments = {};
        const initialUtilities = {};
        
        if (response.data.room?.assignedGuardiansRents) {
          response.data.room.assignedGuardiansRents.forEach((rent, index) => {
            const guardianId = rent.guardian.id;
            initialPayments[guardianId] = {
              license_fee: rent.license_fee || rent.room?.price || 0,
              damage_security_payment: rent.damage_security_payment || rent.room?.price || 0
            };
            initialUtilities[guardianId] = {
              pay_gas: rent.pay_gas || 0,
              pay_electric: rent.pay_electric || 0,
              pay_water: rent.pay_water || 0,
              pay_council_tax: rent.pay_council_tax || 0
            };
          });
        }
        
        setPayments(initialPayments);
        setUtilities(initialUtilities);
      } else {
        throw new Error(response.message || 'Failed to load room data');
      }
    } catch (error) {
      console.error('Error loading room data:', error);
      toast.error('Failed to load room data');
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    loadRoomData();
  }, [loadRoomData]);

  const handlePropertySearch = async (searchTerm) => {
    setPropertySearch(searchTerm);
    if (searchTerm.length > 2) {
      try {
        const response = await roomManageService.searchProperties(searchTerm);
        if (response.success) {
          setProperty(response.data.property);
          setAvailableRooms(response.data.rooms || []);
        }
      } catch (error) {
        console.error('Error searching properties:', error);
      }
    }
  };

  const handleRoomSelection = (selectedRoom) => {
    setSelectedRoom(selectedRoom);
  };

  const handleGuardianSelection = (guardianId, isSelected) => {
    if (isSelected) {
      setSelectedGuardians(prev => [...prev, guardianId]);
    } else {
      setSelectedGuardians(prev => prev.filter(id => id !== guardianId));
    }
  };

  const handlePaymentChange = (guardianId, field, value) => {
    setPayments(prev => ({
      ...prev,
      [guardianId]: {
        ...prev[guardianId],
        [field]: value
      }
    }));
  };

  const handleUtilityChange = (guardianId, field, value) => {
    setUtilities(prev => ({
      ...prev,
      [guardianId]: {
        ...prev[guardianId],
        [field]: parseInt(value)
      }
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate guardian officer selection
    if (!guardianOfficerId) {
      newErrors.guardian_officer_id = 'Guardian officer is required';
    }

    // Validate property selection
    if (!property) {
      newErrors.property = 'Property is missing. Please search for property.';
    }

    // Validate room selection
    if (!selectedRoom) {
      newErrors.room = 'Room is missing. Please select one from available rooms in property.';
    }

    // Validate guardian selection
    if (!selectedGuardians || selectedGuardians.length === 0) {
      newErrors.selectedGuardians = 'Select at least one guardian to move.';
    }

    // Validate payment fields for each selected guardian
    selectedGuardians.forEach(guardianId => {
      const guardianPayments = payments[guardianId];
      if (!guardianPayments) {
        newErrors[`payments_${guardianId}`] = 'Payment information is required for selected guardian';
        return;
      }

      if (!guardianPayments.license_fee || guardianPayments.license_fee <= 0) {
        newErrors[`license_fee_${guardianId}`] = 'Monthly License Fee is required and must be greater than 0';
      }

      if (!guardianPayments.damage_security_payment || guardianPayments.damage_security_payment <= 0) {
        newErrors[`damage_security_payment_${guardianId}`] = 'Key Payment is required and must be greater than 0';
      }

      // Validate utility fields
      const guardianUtilities = utilities[guardianId];
      if (guardianUtilities) {
        const utilityFields = ['pay_gas', 'pay_electric', 'pay_water', 'pay_council_tax'];
        utilityFields.forEach(field => {
          if (guardianUtilities[field] === undefined || guardianUtilities[field] === null) {
            newErrors[`${field}_${guardianId}`] = `${field.replace('_', ' ')} liability is required`;
          }
        });
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      toast.error('Please fix the validation errors before submitting');
      return;
    }
    
    try {
      const formData = {
        room_id: room?.id,
        propertyId: property?.id,
        selectedGuardians,
        guardian_officer_id: guardianOfficerId,
        key_number: selectedRoom?.key_number,
        payments,
        utilities
      };

      console.log('Submitting move guardian form:', formData);

      const response = await roomManageService.moveGuardian(formData);
      
      if (response.success) {
        toast.success(response.message || 'Guardian moved successfully!');
        
        // Laravel behavior: if multiple guardians moved, go to room show; if single guardian, go to guardian show
        if (selectedGuardians.length === 1) {
          navigate(`/guardians/${selectedGuardians[0]}`);
        } else {
          navigate(`/rooms/${room.id}`);
        }
      } else {
        setErrors(response.errors || {});
        toast.error(response.message || 'Failed to move guardian');
      }
    } catch (error) {
      console.error('Error moving guardian:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
      toast.error(error.response?.data?.message || 'Failed to move guardian');
    }
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading room data...</p>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <p>Room not found</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f0f4f8', minHeight: '100vh', width: '100%' }}>
      <div className={styles.container}>
        {/* Breadcrumb */}
        <div className={styles.breadcrumbContainer}>
        <nav aria-label="breadcrumb">
          <ol className={styles.breadcrumb}>
            <li className={styles.breadcrumbItem}>
              <Link to="/dashboard">Dashboard</Link>
            </li>
            <li className={styles.breadcrumbItem}>
              <Link to="/properties">Properties</Link>
            </li>
            <li className={styles.breadcrumbItem}>
              <Link to={`/properties/${room.property?.id}`}>{room.property?.name}</Link>
            </li>
            <li className={`${styles.breadcrumbItem} ${styles.active}`}>
              Manage Room {room.name}
            </li>
          </ol>
        </nav>
      </div>

      {/* JavaScript ID for form */}
      <div id="js-assign-guardian-form"></div>

      {/* Top Section */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panelTitle}>
            <h3>Assign Guardian to property</h3>
          </div>
          <div className={styles.panelGroup}>
            <strong>Step 1</strong> Choose property by typing its name or ID. Property details will appear.<br />
            <strong>Step 2</strong> Select available room by clicking on button.<br />
            <strong>Step 3</strong> Confirm by clicking on Assign Guardian button.<br />
            <strong>Step 4</strong> Select guardian officer.<br />
            <strong>Step 5</strong> Select second Guardian if required.<br />
          </div>
        </div>
      </div>

      <div className={styles.row}>
        {/* Left Column - Property Selection */}
        <div className={styles.colMd4}>
          <div className={styles.panelTitle}>
            <h3>Choose property</h3>
          </div>
          <div className={styles.panelGroup}>
            <label htmlFor="property">Search for property</label>
            <input
              type="text"
              id="property"
              className={`${styles.formControl} typeahead-property`}
              placeholder="Type property ID or name (or *)"
              value={propertySearch}
              onChange={(e) => handlePropertySearch(e.target.value)}
              style={{ height: '50px' }}
            />
          </div>

          {/* Guardian Selection */}
          {room.assignedGuardiansRents?.map((rent, key) => (
            <GuardianCard
              key={rent?.guardian?.id || key}
              guardian={rent?.guardian}
              rent={rent}
              index={key}
              moveGuardian={moveGuardian}
              onSelectionChange={handleGuardianSelection}
              selected={selectedGuardians.includes(rent?.guardian?.id)}
            />
          ))}

          {/* Guardian Officer Selection */}
          <div className={styles.panelTitle}>
            <h3 className={styles.labelRequired}>Guardian Officer</h3>
          </div>
          <div className={styles.panelGroup} id="injectBox">
            <div className={styles.row}>
              <div className={styles.colSm10}>
                <select
                  className={`${styles.formControl} ${errors.guardian_officer_id ? styles.error : ''}`}
                  value={guardianOfficerId}
                  onChange={(e) => setGuardianOfficerId(e.target.value)}
                  required
                >
                  <option value="">Select guardian officer</option>
                  {officers.map(officer => (
                    <option key={officer.id} value={officer.id}>
                      {officer.name}
                    </option>
                  ))}
                </select>
                {errors.guardian_officer_id && (
                  <div className={styles.errorMessage}>{errors.guardian_officer_id}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Property Overview */}
        <div className={styles.colMd8}>
          <div className={styles.panelTitle}>
            <h3>Property overview</h3>
          </div>
          <div className={styles.panelGroup} id="injectBox">
            {property && <PropertyCard property={property} />}
          </div>
        </div>
      </div>

      {/* Available Rooms */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={styles.panelTitle}>
            <h3>Available rooms</h3>
          </div>
          <div className={styles.panelGroup} id="injectBox">
            {property && (
              <div className={styles.roomsGrid}>
                {availableRooms.map(room => (
                  <RoomCard
                    key={room.id}
                    room={room}
                    property={property}
                    onSelect={handleRoomSelection}
                    selected={selectedRoom?.id === room.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Update License Fees */}
      <div className={styles.row}>
        <div className={styles.colMd12}>
          <div className={`${styles.panelTitle} ${styles.titleStandard} ${styles.mb10}`}>
            <h3>Update License Fees</h3>
          </div>
          <div className={styles.row}>
            {room.assignedGuardiansRents?.map((rent, key) => (
              <div key={rent?.guardian?.id || key} className={styles.colSm6}>
                       <PaymentSection
                         rent={rent}
                         index={key}
                         moveGuardian={moveGuardian}
                         payments={payments[rent?.guardian?.id] || {}}
                         utilities={utilities[rent?.guardian?.id] || {}}
                         onPaymentChange={(field, value) => handlePaymentChange(rent?.guardian?.id, field, value)}
                         onUtilityChange={(field, value) => handleUtilityChange(rent?.guardian?.id, field, value)}
                         disabled={disabledRents.includes(rent?.id)}
                         errors={errors}
                       />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Confirmation Section */}
      <form onSubmit={handleSubmit}>
        <div className={styles.row}>
          <div className={styles.colMd12}>
            <div className={`${styles.panelTitle} ${styles.titleStandard}`}>
              <h3>Confirmation</h3>
            </div>
            <div className={`${styles.panelGroup} ${styles.borderDown} ${styles.marginDown}`}>
              <div className={styles.row}>
                <div className={styles.colMd4}>
                  <SelectedGuardianCard guardians={selectedGuardians} room={room} />
                </div>
                <div className={styles.colMd4}>
                  <SelectedPropertyCard property={property} />
                </div>
                <div className={styles.colMd4}>
                  <SelectedRoomCard room={selectedRoom} />
                </div>
              </div>

                     {/* Error Display */}
                     {Object.keys(errors).length > 0 && (
                       <div className={styles.colMd12} style={{ marginTop: '20px' }}>
                         {Object.entries(errors).map(([field, error]) => (
                           <div key={field} className={styles.alertDanger}>
                             {error}
                           </div>
                         ))}
                       </div>
                     )}

                     {/* Global Form Errors */}
                     {errors.selectedGuardians && (
                       <div className={styles.alertDanger}>
                         {errors.selectedGuardians}
                       </div>
                     )}
                     {errors.property && (
                       <div className={styles.alertDanger}>
                         {errors.property}
                       </div>
                     )}
                     {errors.room && (
                       <div className={styles.alertDanger}>
                         {errors.room}
                       </div>
                     )}

              <div className={styles.colMd12}>
                <input type="hidden" name="guardian_officer_id" value={guardianOfficerId} />
                <input type="hidden" name="room_id" value={room?.id} className={styles.selectedRoomRoomId} />
                <input type="hidden" name="propertyId" value={property?.id} />
                <input type="hidden" name="key_number" value={selectedRoom?.key_number} className={styles.selectedRoomRoomKeyNumber} />

                <button
                  className={`${styles.btn} ${styles.btnHead} ${styles.btnStandard} ${styles.pullRight} ${styles.jsMoveSubmit}`}
                  type="submit"
                  data-confirm-action="Are you sure you want to move guardian?"
                >
                  Move Guardian
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
      </div>
    </div>
  );
};

// Guardian Card Component
const GuardianCard = ({ guardian, rent, index, moveGuardian, onSelectionChange, selected }) => {
  // Add safety checks
  if (!guardian) {
    return (
      <div className={styles.guardianCard}>
        <div className={styles.panelTitle}>
          <h3>Guardian {index + 1} - Data Unavailable</h3>
        </div>
        <div className={styles.panelGroup}>
          <p>Guardian data is not available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.guardianCard}>
      <div className={styles.panelTitle}>
        <h3>Guardian {index + 1}</h3>
      </div>
      <div className={styles.panelGroup}>
        {moveGuardian && (
          <div className={styles.row} style={{ textAlign: 'center' }}>
            <fieldset className={styles.formHorizontal}>
              <div className={styles.formGroup}>
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={(e) => onSelectionChange(guardian.id, e.target.checked)}
                  className={styles.guardians}
                />
                <label className={styles.controlLabel}>Select Guardian to Move</label>
              </div>
            </fieldset>
          </div>
        )}

        <div className={styles.row} style={{ textAlign: 'center' }}>
          {guardian.user?.image ? (
            <img
              className={styles.imgCircle}
              width="100"
              height="100"
              src={guardian.user.image}
                     alt=""
            />
          ) : (
            <div className={styles.placeholderImage}></div>
          )}
        </div>

        <div className={styles.tableResponsive}>
          <table className={styles.tableList}>
            <tbody>
              <tr>
                <td className={styles.tableListHeader}>Guardian ID:</td>
                <td>{guardian.unique_guardian_number || 'N/A'}</td>
              </tr>
              <tr>
                <td className={styles.tableListHeader}>Name:</td>
                <td>{guardian.user?.present?.fullName || guardian.full_name || 'N/A'}</td>
              </tr>
              <tr>
                <td className={styles.tableListHeader}>Mobile number:</td>
                <td>{guardian.user?.mobile_number || '-'}</td>
              </tr>
              <tr>
                <td className={styles.tableListHeader}>Email:</td>
                <td>{guardian.user?.email || 'N/A'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Property Card Component
const PropertyCard = ({ property }) => {
  return (
    <div className={styles.propertyCard}>
      <dl className={styles.list}>
        <dt>Property ID:</dt>
        <dd>
          <Link to={`/properties/${property.id}`} className={styles.propertyLink}>
            {property.property_id}
          </Link>
        </dd>
        <dt>Property name:</dt>
        <dd>{property.name}</dd>
        <dt>Property address:</dt>
        <dd>{property.address_1}</dd>
        <dt>Postcode:</dt>
        <dd>{property.postcode}</dd>
        {property.date_taken_on && property.date_taken_back && (
          <>
            <dt>Lease dates</dt>
            <dd>
              {property.shouldDisplayDate?.(property.date_taken_on)} to{' '}
              {property.shouldDisplayDate?.(property.date_taken_back)}
            </dd>
          </>
        )}
      </dl>

      <div className={styles.colMd12}>
        <dl className={styles.list}>
          <dt>Property notes:</dt>
          <dd className={styles.shortDescDd}>
            {property.short_description || <br />}
          </dd>
          <dt>Photos:</dt>
          <dd>
            {property.photos?.length > 0 && (
              <div className={styles.propertyPhotos}>
                {property.photos.map((photo, index) => (
                  <img
                    key={index}
                    src={photo.name}
                    className={styles.imgProperty}
                    alt={`Property photo ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </dd>
        </dl>
      </div>
    </div>
  );
};

// Room Card Component
const RoomCard = ({ room, property, onSelect, selected }) => {
  return (
    <div className={`${styles.colXs12} ${styles.colSm6} ${styles.colMd4}`}>
      <div className={`${styles.roomInformation} ${room.roomClass} ${selected ? styles.selected : ''}`}>
        <div style={{ display: 'none' }} id={`room-number-${room.id}`}>
          {room.id}
        </div>
        <div style={{ display: 'none' }} id={`room-name-${room.id}`}>
          {room.name}
        </div>
        <div style={{ display: 'none' }} id={`key-number-${room.id}`}>
          {room.key_number}
        </div>

        <p className={styles.roomNumber}>Room {room.name}</p>
        <ul className={styles.roomInformationIn} id={`room-information-in-${room.id}`}>
          {!room.isEmpty() ? (
            room.assignedGuardiansRents?.map((rent, index) => (
              <li key={index}>
                <i className={styles.glyphiconUser}></i>
                <Link to={`/guardians/${rent.guardian.id}`}>
                  {String(rent.guardian.fullName || 'Unknown Guardian')}
                </Link>
              </li>
            ))
          ) : (
            <li>
              <i className={styles.glyphiconUser}></i>Empty room
            </li>
          )}

          <li>
            <span style={{ width: '50%' }}>Numbers of keys: </span>
            {room.key_number}
          </li>

          {property?.isActive() && room.canHaveGuardianAssigned() && (
            <li id={`label-assign-to-${room.id}`} className={styles.assignButton}>
              <button
                id={`button-assign-to-${room.id}`}
                className={styles.labelAssignTo}
                onClick={() => onSelect(room)}
              >
                <span>
                  <i className={styles.glyphiconMenuRight}></i>
                </span>
                Assign here
              </button>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

// Payment Section Component
const PaymentSection = ({ rent, index, moveGuardian, payments, utilities, onPaymentChange, onUtilityChange, disabled, errors }) => {
  // Add safety checks for rent object
  if (!rent || !rent.guardian) {
    return (
      <div className={styles.paymentSection}>
        <div className={styles.panelTitle}>
          <h3>Guardian #{index + 1} - Data Unavailable</h3>
        </div>
        <div className={styles.panelGroup}>
          <p>Guardian data is not available</p>
        </div>
      </div>
    );
  }

  // Calculate total for summary
  const licenseFee = parseFloat(payments.license_fee) || 0;
  const keyPayment = parseFloat(payments.damage_security_payment) || 0;
  const total = licenseFee + keyPayment;

  return (
    <div className={styles.paymentSection}>
      <div className={styles.panelTitle}>
        <h3>
          Guardian #{index + 1}
          {moveGuardian && (
            <span id={`state-${rent.guardian.id}`} style={{ color: 'red' }}>
              - Moving
            </span>
          )}
        </h3>
      </div>
      
      <div className={styles.panelGroup}>
        {/* Guardian and Property Details */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '10px' }}>
            <span style={{ fontWeight: 'bold', color: '#495057' }}>Name: </span>
            <span style={{ color: '#17a2b8' }}>{String(rent.guardian.full_name || 'N/A')} ({String(rent.guardian.unique_guardian_number || 'N/A')})</span>
          </div>
          <div>
            <span style={{ fontWeight: 'bold', color: '#495057' }}>Property: </span>
            <span style={{ color: '#17a2b8' }}>{rent.property?.name || 'N/A'}</span>
          </div>
        </div>

        {/* Suggested Room Price */}
        <div style={{ textAlign: 'center', color: '#6c757d', marginBottom: '20px', fontWeight: 'bold' }}>
          Suggested Room Price is: £{rent.room?.price || 0}
        </div>

        <hr style={{ border: '1px solid #dee2e6', margin: '20px 0' }} />

        {/* License Fee and Key Payment */}
        <div style={{ marginBottom: '20px' }}>
                 <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                   <label style={{ fontWeight: 'bold', color: '#495057', width: '200px', textAlign: 'left' }}>
                     Monthly License Fee *
                   </label>
                   <div style={{ marginLeft: '10px' }}>
                     <input
                       type="text"
                       className={`${styles.formControl} ${errors[`license_fee_${rent.guardian.id}`] ? styles.error : ''}`}
                       value={payments.license_fee || ''}
                       onChange={(e) => onPaymentChange('license_fee', e.target.value)}
                       disabled={disabled}
                       required
                       style={{ width: '150px' }}
                     />
                     {errors[`license_fee_${rent.guardian.id}`] && (
                       <div className={styles.errorMessage}>{errors[`license_fee_${rent.guardian.id}`]}</div>
                     )}
                   </div>
                 </div>
                 
                 <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                   <label style={{ fontWeight: 'bold', color: '#495057', width: '200px', textAlign: 'left' }}>
                     Key Payment *
                   </label>
                   <div style={{ marginLeft: '10px' }}>
                     <input
                       type="text"
                       className={`${styles.formControl} ${errors[`damage_security_payment_${rent.guardian.id}`] ? styles.error : ''}`}
                       value={payments.damage_security_payment || ''}
                       onChange={(e) => onPaymentChange('damage_security_payment', e.target.value)}
                       disabled={disabled}
                       required
                       style={{ width: '150px' }}
                     />
                     {errors[`damage_security_payment_${rent.guardian.id}`] && (
                       <div className={styles.errorMessage}>{errors[`damage_security_payment_${rent.guardian.id}`]}</div>
                     )}
                   </div>
                 </div>
        </div>

        <hr style={{ border: '1px solid #dee2e6', margin: '20px 0' }} />

        {/* Summary */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          <label style={{ fontWeight: 'bold', color: '#495057', width: '200px', textAlign: 'left' }}>
            Summary
          </label>
          <div style={{ fontWeight: 'bold', color: '#495057', marginLeft: '10px' }}>
            £{total.toFixed(1)}
          </div>
        </div>

        <hr style={{ border: '1px solid #dee2e6', margin: '20px 0' }} />

        {/* Utility Liability Section */}
        <div style={{ marginBottom: '20px' }}>
          <p style={{ fontWeight: 'bold', color: '#495057', marginBottom: '15px' }}>
            Accept the liability to pay the following (tick if applicable):
          </p>
          
                 <UtilitiesSection
                   guardianId={rent.guardian.id}
                   utilities={utilities}
                   onUtilityChange={onUtilityChange}
                   disabled={disabled}
                   errors={errors}
                 />
        </div>
      </div>
    </div>
  );
};

// Utilities Section Component
const UtilitiesSection = ({ guardianId, utilities, onUtilityChange, disabled, errors }) => {
  const utilityFields = [
    { key: 'pay_gas', label: 'Gas' },
    { key: 'pay_electric', label: 'Electric' },
    { key: 'pay_water', label: 'Water' },
    { key: 'pay_council_tax', label: 'Council Tax' }
  ];

  return (
    <div className={styles.utilitiesSection}>
         {utilityFields.map(field => (
           <div key={field.key} style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
             <span style={{ fontWeight: 'bold', color: '#495057', width: '200px', textAlign: 'left' }}>
               {field.label}
             </span>
             <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
               <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                 <input
                   type="radio"
                   name={`payments[${guardianId}][${field.key}]`}
                   value="0"
                   checked={utilities[field.key] === 0}
                   onChange={(e) => onUtilityChange(field.key, e.target.value)}
                   disabled={disabled}
                   className={`${styles.formCheckbox} ${errors[`${field.key}_${guardianId}`] ? styles.error : ''}`}
                 />
                 No
               </label>
               <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                 <input
                   type="radio"
                   name={`payments[${guardianId}][${field.key}]`}
                   value="1"
                   checked={utilities[field.key] === 1}
                   onChange={(e) => onUtilityChange(field.key, e.target.value)}
                   disabled={disabled}
                   className={`${styles.formCheckbox} ${errors[`${field.key}_${guardianId}`] ? styles.error : ''}`}
                 />
                 Yes
               </label>
             </div>
             {errors[`${field.key}_${guardianId}`] && (
               <div className={styles.errorMessage}>{errors[`${field.key}_${guardianId}`]}</div>
             )}
           </div>
         ))}
    </div>
  );
};

// Selected Guardian Card Component
const SelectedGuardianCard = ({ guardians, room }) => {
  return (
    <div className={styles.colMd4}>
      <div className={`${styles.well} ${styles.wellSuccess} ${styles.confirmationBox}`}>
        <label htmlFor="selectedGuardian" style={{ fontWeight: 'bold', color: '#495057', fontSize: '16px', marginBottom: '10px', display: 'block' }}>Selected Guardian</label>
        <dl className={`${styles.list} ${styles.assignGuardian} ${styles.greenListWithWidthSmaller}`} style={{ marginTop: '10px' }} id="selectedGuardian">
          {room?.assignedGuardiansRents?.map((rent, index) => (
            <span key={rent?.guardian?.id} id={`selected-guardian-${rent?.guardian?.id}`}>
              <dt>Name:</dt>
              <dd style={{ fontWeight: 'bold', color: '#495057' }}>{String(rent?.guardian?.full_name || 'N/A')}</dd>
            </span>
          ))}
        </dl>
      </div>
    </div>
  );
};

// Selected Property Card Component
const SelectedPropertyCard = ({ property }) => {
  return (
    <div className={styles.colMd4}>
      <div className={`${styles.well} ${styles.confirmationBox}`}>
        <label htmlFor="selectedProperty" style={{ fontWeight: 'bold', color: '#495057', fontSize: '16px', marginBottom: '10px', display: 'block' }}>Selected property from available</label>
        <dl className={`${styles.list} ${styles.assignGuardian} ${styles.greenListWithWidthSmaller}`} style={{ marginTop: '10px' }} id="selectedProperty">
          {property ? (
            <>
              <dt>Property ID:</dt>
              <dd style={{ fontWeight: 'bold', color: '#495057' }}>{property.property_id}</dd>
              <dt>Name:</dt>
              <dd style={{ fontWeight: 'bold', color: '#495057' }}>{property.name}</dd>
            </>
          ) : (
            <>
              <dt>Property ID:</dt>
              <dd style={{ fontWeight: 'bold', color: '#dc3545' }}>Missing</dd>
              <dt>Name:</dt>
              <dd style={{ fontWeight: 'bold', color: '#dc3545' }}>Missing</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
};

// Selected Room Card Component
const SelectedRoomCard = ({ room }) => {
  return (
    <div className={styles.colMd4}>
      <div className={`${styles.well} ${styles.confirmationBox}`} id="roomsWell">
        <label htmlFor="selectedRoomBox" style={{ fontWeight: 'bold', color: '#495057', fontSize: '16px', marginBottom: '10px', display: 'block' }}>Selected room in property</label>
        <dl className={`${styles.list} ${styles.assignGuardian} ${styles.greenListWithWidthSmaller}`} style={{ marginTop: '10px' }} id="selectedRoomBox">
          <dt>Room:</dt>
          <dd id="selectedRoom-room-name">
            {room ? (
              <span style={{ fontWeight: 'bold', color: '#495057' }}>{room.name}</span>
            ) : (
              <span style={{ fontWeight: 'bold', color: '#dc3545' }}>Missing</span>
            )}
          </dd>
          <dt>Keys:</dt>
          <dd id="selectedRoom-room-key-number">
            {room ? (
              <span style={{ fontWeight: 'bold', color: '#495057' }}>{room.key_number}</span>
            ) : (
              <span style={{ fontWeight: 'bold', color: '#dc3545' }}>Missing</span>
            )}
          </dd>
        </dl>
      </div>
    </div>
  );
};

export default RoomManage; 